import { useEffect, useRef } from 'react';
import echo from '../echo';
import { WHITELABEL_ID } from '../constants';

export const MANAGER_PICKUP_CREATED_EVENT =
  '.App\\Events\\Whitelabel\\Manager\\PickupCreated';
export const MANAGER_PICKUP_UPDATED_EVENT =
  '.App\\Events\\Whitelabel\\Manager\\PickupUpdated';

export const getManagerPickupsChannel = (whitelabelId) =>
  `np-whitelabel-${whitelabelId}-manager-pickups`;

const removeSocketListener = (socket, event, listener) => {
  if (typeof socket?.off === 'function') {
    socket.off(event, listener);
    return;
  }

  socket?.removeListener?.(event, listener);
};

const useLatestHandlers = (handlers) => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  return handlersRef;
};

export const useManagerPickupEvents = ({
  enabled = true,
  whitelabelId = WHITELABEL_ID,
  onCreated,
  onUpdated,
  onReconnect,
} = {}) => {
  const handlersRef = useLatestHandlers({ onCreated, onUpdated, onReconnect });

  useEffect(() => {
    if (!enabled || !whitelabelId) return undefined;

    const channelName = getManagerPickupsChannel(whitelabelId);
    const channel = echo.channel(channelName);

    channel.listen(MANAGER_PICKUP_CREATED_EVENT, (event) => {
      if (event?.message_type === 'pickup_created' && event.pickup) {
        handlersRef.current.onCreated?.(event);
      }
    });

    channel.listen(MANAGER_PICKUP_UPDATED_EVENT, (event) => {
      if (event?.message_type === 'pickup_updated' && event.pickup) {
        handlersRef.current.onUpdated?.(event);
      }
    });

    const socket = echo?.connector?.socket;
    const handleReconnect = () => handlersRef.current.onReconnect?.();
    socket?.on?.('connect', handleReconnect);

    return () => {
      removeSocketListener(socket, 'connect', handleReconnect);
      echo.leave(channelName);
    };
  }, [enabled, handlersRef, whitelabelId]);
};

export const usePickupUpdateEvent = ({
  channelId,
  enabled = true,
  onUpdated,
  onReconnect,
} = {}) => {
  const handlersRef = useLatestHandlers({ onUpdated, onReconnect });

  useEffect(() => {
    if (!enabled || !channelId) return undefined;

    const channel = echo.channel(channelId);

    channel.listen(MANAGER_PICKUP_UPDATED_EVENT, (event) => {
      if (event?.message_type === 'pickup_updated' && event.pickup) {
        handlersRef.current.onUpdated?.(event);
      }
    });

    const socket = echo?.connector?.socket;
    const handleReconnect = () => handlersRef.current.onReconnect?.();
    socket?.on?.('connect', handleReconnect);

    return () => {
      removeSocketListener(socket, 'connect', handleReconnect);
      echo.leave(channelId);
    };
  }, [channelId, enabled, handlersRef]);
};
